import type { ColumnDef, SortingFn } from "@tanstack/react-table";
import type { Table as MdastTable } from "mdast";
import * as React from "react";
import { cn } from "src/common/utils";
import { SimpleDataTable } from "src/components/ui/SimpleDataTable";

export type MarkdownTableCell = {
  display: React.ReactNode;
  sortValue: string;
};

export type MarkdownTableRow = {
  cells: MarkdownTableCell[];
};

type TableAlignment = "left" | "right" | "center";

const smartSortingFn: SortingFn<MarkdownTableRow> = (rowA, rowB, columnId) => {
  const a = rowA.getValue<string>(columnId);
  const b = rowB.getValue<string>(columnId);

  const numA = parseFloat(a);
  const numB = parseFloat(b);

  if (!isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }

  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

const EMPTY_CELL: MarkdownTableCell = {
  display: null,
  sortValue: "",
};

const DEFAULT_EMPTY_STATE = <span className="text-muted-foreground">No data</span>;

type MarkdownTableProps = {
  node?: MdastTable;
  children: React.ReactNode;
};

const TEXT_WHITESPACE = /\s+/g;

function extractText(value: React.ReactNode): string {
  if (value === null || value === undefined || typeof value === "boolean") {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(extractText).join(" ").replace(TEXT_WHITESPACE, " ").trim();
  }

  if (React.isValidElement(value)) {
    return extractText(value.props.children);
  }

  return "";
}

function toElementArray(value: React.ReactNode): React.ReactElement[] {
  return React.Children.toArray(value).filter((child): child is React.ReactElement => React.isValidElement(child));
}

function toCell(cellElement: React.ReactElement): MarkdownTableCell {
  const rawContent = React.Children.toArray(cellElement.props.children);
  const display = rawContent.length <= 1 ? rawContent[0] ?? null : <>{rawContent}</>;

  return {
    display,
    sortValue: extractText(rawContent),
  };
}

function buildRows(children: React.ReactNode): { header: MarkdownTableCell[]; body: MarkdownTableRow[] } {
  let headerCells: MarkdownTableCell[] = [];
  const bodyRows: MarkdownTableRow[] = [];

  const recordRow = (rowElement: React.ReactElement, target: "header" | "body") => {
    const cellElements = toElementArray(rowElement.props.children);
    const cells = cellElements.map(toCell);

    if (target === "header") {
      headerCells = cells;
    } else if (cells.length > 0) {
      bodyRows.push({ cells });
    }
  };

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }

    const elementType = typeof child.type === "string" ? child.type : undefined;

    if (elementType === "thead") {
      toElementArray(child.props.children).forEach((row) => recordRow(row, "header"));
      return;
    }

    if (elementType === "tbody") {
      toElementArray(child.props.children).forEach((row) => recordRow(row, "body"));
      return;
    }

    if (elementType === "tr") {
      if (headerCells.length === 0) {
        recordRow(child, "header");
      } else {
        recordRow(child, "body");
      }
    }
  });

  return { header: headerCells, body: bodyRows };
}

function createColumns(
  headerCells: MarkdownTableCell[],
  alignments: ReadonlyArray<TableAlignment | null>,
): ColumnDef<MarkdownTableRow>[] {
  const columnCount = Math.max(headerCells.length, 0);

  const normalizedHeaders = Array.from({ length: columnCount }, (_, index) =>
    headerCells[index] ?? {
      display: `Column ${index + 1}`,
      sortValue: `Column ${index + 1}`,
    },
  );

  return normalizedHeaders.map<ColumnDef<MarkdownTableRow>>((header, index) => {
    const alignment = alignments[index];
    const alignmentClass = alignment === "right" ? "text-right" : alignment === "center" ? "text-center" : "text-left";
    const headerLabel = header.sortValue || `Column ${index + 1}`;

    return {
      id: `col-${index}`,
      accessorFn: (row) => row.cells[index]?.sortValue ?? "",
      header: headerLabel,
      cell: ({ row }) => (
        <div className={cn(alignmentClass)}>{row.original.cells[index]?.display ?? EMPTY_CELL.display}</div>
      ),
      meta: {
        headerClassName: cn("px-4", alignmentClass),
        cellClassName: cn("px-4 text-muted-foreground whitespace-normal break-words", alignmentClass),
      },
      enableSorting: true,
      sortingFn: smartSortingFn,
    } satisfies ColumnDef<MarkdownTableRow>;
  });
}

function createData(rows: MarkdownTableRow[], columnCount: number): MarkdownTableRow[] {
  return rows.map((row) => ({
    cells: Array.from({ length: columnCount }, (_, index) => row.cells[index] ?? EMPTY_CELL),
  }));
}

export function MarkdownTable({ node, children }: MarkdownTableProps) {
  const { header, body } = buildRows(children);
  const columnCount = Math.max(header.length, ...body.map((row) => row.cells.length), 0);
  const alignments = React.useMemo<Array<TableAlignment | null>>(() => {
    if (node && Array.isArray(node.align)) {
      return node.align.map((value) =>
        value === "left" || value === "right" || value === "center" ? value : null,
      );
    }
    return [];
  }, [node]);

  const columns = React.useMemo(() => createColumns(header, alignments), [alignments, header]);
  const data = React.useMemo(() => createData(body, columnCount), [body, columnCount]);

  // w-[1px] min-w-full: concrete width prevents table from expanding the flex parent;
  // overflow-x-auto enables horizontal scrolling for wide tables
  return (
    <div className="overflow-x-auto w-[1px] min-w-full">
      <SimpleDataTable<MarkdownTableRow>
        data={data}
        columns={columns}
        enableSorting
        enableCellFormatting
        getRowId={(_, index) => `markdown-row-${index}`}
        appearance="clean"
        emptyState={DEFAULT_EMPTY_STATE}
      />
    </div>
  );
}

export function isMdastTableNode(node: unknown): node is MdastTable {
  return Boolean(node && typeof node === "object" && (node as MdastTable).type === "table");
}
